import { useMutation, useQuery } from '@tanstack/react-query'
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { getAdminOrgScope } from '#/shared/api/admin-org-scope'
import type {
  AvailablePremiumNumber,
  BuyPremiumRequest,
  TelnyxCampaignResponse,
  ConversationItemSchema,
  ConversationListItem,
  PageTemplateListItem,
  PageUserPhoneEventResponse,
} from './models'
import type { LiveCallRead, MessageSavingsResponse } from './models'
import type {
  CampaignActivityRead,
  CampaignAudienceCount,
  CampaignAudienceQuery,
  CampaignContactsRead,
  CampaignCreate,
  CampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGetParams,
  CampaignDeliverabilityRead,
  CampaignFilterField,
  CampaignFilterFieldsApiV1CampaignsFilterFieldsGetParams,
  CampaignIndexRead,
  CampaignOverviewRead,
  CampaignPeopleAdd,
  CampaignPeopleAddResult,
  CampaignPeopleAssign,
  CampaignPeopleAssignResult,
  CampaignPeopleRemove,
  CampaignPeopleRemoveResult,
  CampaignPeopleTasks,
  CampaignPeopleTasksResult,
  CampaignPowerDialApiV1CampaignsCampaignIdPowerDialPostParams,
  CampaignRead,
  CampaignSequenceRead,
  CampaignStepCreate,
  CampaignStepDraft,
  CampaignStepPreview,
  CampaignStepRead,
  CampaignStepUpdate,
  CampaignSummaryRead,
  CampaignTemplateOption,
  CampaignTemplatesApiV1CampaignsTemplatesGetParams,
  CampaignUpdate,
  ListActivityApiV1CampaignsCampaignIdActivityGetParams,
  ListCampaignRowsApiV1CampaignsRowsGetParams,
  ListContactsApiV1CampaignsCampaignIdContactsGetParams,
  MassCallResponse,
  StartCampaignApiV1CampaignsCampaignIdStartPostBody,
} from './models'
import { serviceClient } from '#/shared/api/http-client'

const BASE = '/communication/admin/api/v1/product'

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyResponse = AxiosResponse<any>
type QueryOpts = { query?: Partial<UseQueryOptions<AnyResponse>> & { enabled?: boolean } }
type MutationOpts<V> = { mutation?: UseMutationOptions<AnyResponse, unknown, V> }

function orgParams(extra?: Record<string, unknown>): Record<string, unknown> {
  const organization_id = getAdminOrgScope()
  return { ...(organization_id ? { organization_id } : {}), ...extra }
}

export function useListTemplatesApiV1TemplatesGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<PageTemplateListItem>>({
    queryKey: [`/api/v1/templates`, params],
    queryFn: () => serviceClient.get(`${BASE}/templates`, { params: orgParams(params) }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<PageTemplateListItem>>>),
  })
}

export function getGetTemplateApiV1TemplatesTemplateIdGetQueryOptions(
  templateId: string,
  options?: QueryOpts,
) {
  return {
    queryKey: [`/api/v1/templates/${templateId}`],
    queryFn: () => serviceClient.get(`${BASE}/templates/${templateId}`, { params: orgParams() }),
    ...options?.query,
  }
}

export const getGetTemplateApiV1TemplatesTemplateIdGetQueryKey = (templateId: string) => [
  `/api/v1/templates/${templateId}`,
]

export function useGetTemplateApiV1TemplatesTemplateIdGet(
  templateId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getGetTemplateApiV1TemplatesTemplateIdGetQueryKey(templateId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/templates/${templateId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useCreateTemplateApiV1TemplatesPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/templates`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateTemplateApiV1TemplatesTemplateIdPatch(
  options?: MutationOpts<{ templateId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/templates/${templateId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useDeleteTemplateApiV1TemplatesTemplateIdDelete(
  options?: MutationOpts<{ templateId: string }>,
) {
  return useMutation({
    mutationFn: ({ templateId }: { templateId: string }) =>
      serviceClient.delete(`${BASE}/templates/${templateId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function getRenderTemplateApiV1TemplatesTemplateIdRenderGetQueryOptions(
  templateId: string,
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return {
    queryKey: [`/api/v1/templates/${templateId}/render`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/templates/${templateId}/render`, { params: orgParams(params) }),
    ...options?.query,
  }
}

export const getListAutomationsApiV1AutomationsGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/automations`, ...(params ? [params] : [])]

export function useListAutomationsApiV1AutomationsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getListAutomationsApiV1AutomationsGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/automations`, { params: orgParams(params), signal }),
    ...options?.query,
  })
}

export const getGetAutomationApiV1AutomationsAutomationIdGetQueryKey = (
  automationId: string,
) => [`/api/v1/automations/${automationId}`]

export function useGetAutomationApiV1AutomationsAutomationIdGet(
  automationId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getGetAutomationApiV1AutomationsAutomationIdGetQueryKey(automationId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/automations/${automationId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useCreateAutomationApiV1AutomationsPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/automations`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateAutomationApiV1AutomationsAutomationIdPut(
  options?: MutationOpts<{ automationId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ automationId, data }: { automationId: string; data: unknown }) =>
      serviceClient.put(`${BASE}/automations/${automationId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useDeleteAutomationApiV1AutomationsAutomationIdDelete(
  options?: MutationOpts<{ automationId: string }>,
) {
  return useMutation({
    mutationFn: ({ automationId }: { automationId: string }) =>
      serviceClient.delete(`${BASE}/automations/${automationId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export const getListJobsApiV1SendMassJobsGetQueryKey = (params?: Record<string, unknown>) => [
  `/api/v1/send/mass/jobs`,
  ...(params ? [params] : []),
]

export function useListJobsApiV1SendMassJobsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getListJobsApiV1SendMassJobsGetQueryKey(params),
    queryFn: () => serviceClient.get(`${BASE}/send/mass/jobs`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function previewMassEmailApiV1SendMassEmailPreviewPost(
  data: unknown,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.post(`${BASE}/send/mass/email/preview`, data, { params: orgParams(), signal })
}

export function previewMassSmsApiV1SendMassSmsPreviewPost(
  data: unknown,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.post(`${BASE}/send/mass/sms/preview`, data, { params: orgParams(), signal })
}

export function previewSmsCostApiV1SmsMessagesPreviewPost(
  data: unknown,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.post(`${BASE}/sms-messages/preview`, data, { params: orgParams(), signal })
}

export function useMyLinesApiV1TelephonyNumbersMineGet(options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: ['admin-telephony-my-lines'],
    queryFn: () => Promise.resolve({ data: [] } as unknown as AnyResponse),
    ...options?.query,
  })
}

export function useSetDefaultNumberApiV1TelephonyNumbersPhoneIdDefaultPost(
  options?: MutationOpts<{ phoneId: string }>,
) {
  return useMutation({
    mutationFn: (_vars: { phoneId: string }) =>
      Promise.resolve({ data: undefined } as AnyResponse),
    ...options?.mutation,
  })
}

export function useGetEmailEventsApiV1EmailMessagesEmailIdEventsGet(
  emailId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/email-messages/${emailId}/events`],
    queryFn: () =>
      serviceClient.get(`${BASE}/email-messages/${emailId}/events`, { params: orgParams() }),
    ...options?.query,
  })
}

export const getGetJobApiV1SendMassJobsJobIdGetQueryKey = (jobId: string) => [
  `/api/v1/send/mass/jobs/${jobId}`,
]

export function getJobApiV1SendMassJobsJobIdGet(
  jobId: string,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/send/mass/jobs/${jobId}`, { params: orgParams(), signal })
}

export function useGetJobApiV1SendMassJobsJobIdGet(jobId: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: getGetJobApiV1SendMassJobsJobIdGetQueryKey(jobId),
    queryFn: ({ signal }) => getJobApiV1SendMassJobsJobIdGet(jobId, signal),
    ...options?.query,
  })
}

export function listJobsApiV1SendMassJobsGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AxiosResponse<any[]>> {
  return serviceClient.get(`${BASE}/send/mass/jobs`, { params: orgParams(params), signal })
}

export function useCancelJobApiV1SendMassJobsJobIdCancelPost(
  options?: MutationOpts<{ jobId: string }>,
) {
  return useMutation({
    mutationFn: ({ jobId }: { jobId: string }) =>
      serviceClient.post(`${BASE}/send/mass/jobs/${jobId}/cancel`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function usePauseJobApiV1SendMassJobsJobIdPausePost(
  options?: MutationOpts<{ jobId: string }>,
) {
  return useMutation({
    mutationFn: ({ jobId }: { jobId: string }) =>
      serviceClient.post(`${BASE}/send/mass/jobs/${jobId}/pause`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useResumeJobApiV1SendMassJobsJobIdResumePost(
  options?: MutationOpts<{ jobId: string }>,
) {
  return useMutation({
    mutationFn: ({ jobId }: { jobId: string }) =>
      serviceClient.post(`${BASE}/send/mass/jobs/${jobId}/resume`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useStartMassCallApiV1SendMassCallPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/send/mass/call`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useSendMassSmsApiV1SendMassSmsPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/send/mass/sms`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useSendMassEmailApiV1SendMassEmailPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/send/mass/email`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function getNumberHealthApiV1NumberHealthPost(
  data: unknown,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.post(`${BASE}/number-health`, data, { params: orgParams(), signal })
}

export function useUploadFilesApiV1FilesPost(
  options?: { mutation?: UseMutationOptions<AxiosResponse<any[]>, unknown, { data: { files: unknown[] } }> },
) {
  return useMutation({
    mutationFn: ({ data }: { data: { files: unknown[] } }) => {
      const formData = new FormData()
      data.files.forEach((value) => formData.append('files', value as Blob))
      return serviceClient.post<any[]>(`${BASE}/files`, formData, {
        params: orgParams(),
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    ...options?.mutation,
  })
}

export const getListConversationsApiV1ConversationsGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/conversations`, ...(params ? [params] : [])]

export function listConversationsApiV1ConversationsGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AxiosResponse<{ items?: ConversationListItem[]; next_cursor?: string | null }>> {
  return serviceClient.get(`${BASE}/conversations`, { params: orgParams(params), signal })
}

export const getSearchConversationsApiV1ConversationsSearchGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/conversations/search`, ...(params ? [params] : [])]

export function searchConversationsApiV1ConversationsSearchGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AxiosResponse<{ chats: any[]; messages: any[]; next_cursor?: string | null; has_more_chats?: boolean }>> {
  return serviceClient.get(`${BASE}/conversations/search`, { params: orgParams(params), signal })
}

export const getListFavoritesApiV1ConversationsFavoritesGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/conversations/favorites`, ...(params ? [params] : [])]

export const getListBlockedApiV1ConversationsBlockedGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/conversations/blocked`, ...(params ? [params] : [])]

export function useConversationActionApiV1ConversationsActionPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/conversations/action`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useGetConversationApiV1ConversationsConversationIdGet(
  conversationId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/conversations/${conversationId}`],
    queryFn: () =>
      serviceClient.get(`${BASE}/conversations/${conversationId}`, { params: orgParams() }),
    ...options?.query,
  })
}

export const getListConversationItemsApiV1ConversationItemsGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/conversation-items`, ...(params ? [params] : [])]

export function listConversationItemsApiV1ConversationItemsGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AxiosResponse<{ items?: ConversationItemSchema[]; next_cursor?: string | null; prev_cursor?: string | null }>> {
  return serviceClient.get(`${BASE}/conversation-items`, { params: orgParams(params), signal })
}

export function useSendSmsApiV1SendSmsPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/send/sms`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useGetRecordingUrlApiV1CallsCallIdRecordingGet(
  callId: string,
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/calls/${callId}/recording`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/calls/${callId}/recording`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export const getListNumbersApiV1TelephonyNumbersGetQueryKey = (
  params?: Record<string, unknown>,
) => (params ? [`/api/v1/telephony/numbers`, params] : [`/api/v1/telephony/numbers`])

export function useListNumbersApiV1TelephonyNumbersGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getListNumbersApiV1TelephonyNumbersGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/telephony/numbers`, { params: orgParams(params), signal }),
    ...options?.query,
  })
}

export const getListNumberHistoryApiV1TelephonyNumbersHistoryGetQueryKey = (
  params?: Record<string, unknown>,
) =>
  params
    ? [`/api/v1/telephony/numbers/history`, params]
    : [`/api/v1/telephony/numbers/history`]

export function useListNumberHistoryApiV1TelephonyNumbersHistoryGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<PageUserPhoneEventResponse>>({
    queryKey: getListNumberHistoryApiV1TelephonyNumbersHistoryGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/telephony/numbers/history`, {
        params: orgParams(params),
        signal,
      }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<PageUserPhoneEventResponse>>>),
  })
}

export const getGetNumberApiV1TelephonyNumbersPhoneIdGetQueryKey = (phoneId: string) => [
  `/api/v1/telephony/numbers/${phoneId}`,
]

export function useGetNumberApiV1TelephonyNumbersPhoneIdGet(
  phoneId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getGetNumberApiV1TelephonyNumbersPhoneIdGetQueryKey(phoneId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/telephony/numbers/${phoneId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useGetNumberMessagingApiV1TelephonyNumbersPhoneIdMessagingGet(
  phoneId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/telephony/numbers/${phoneId}/messaging`],
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/telephony/numbers/${phoneId}/messaging`, {
        params: orgParams(),
        signal,
      }),
    ...options?.query,
  })
}

export function useUpdateNumberApiV1TelephonyNumbersPhoneIdPatch(
  options?: MutationOpts<{ phoneId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ phoneId, data }: { phoneId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/telephony/numbers/${phoneId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useReleaseMyNumberApiV1TelephonyNumbersPhoneIdDelete(
  options?: MutationOpts<{ phoneId: string }>,
) {
  return useMutation({
    mutationFn: ({ phoneId }: { phoneId: string }) =>
      serviceClient.delete(`${BASE}/telephony/numbers/${phoneId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useAddMemberApiV1TelephonyNumbersPhoneIdMembersPost(
  options?: MutationOpts<{ phoneId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ phoneId, data }: { phoneId: string; data: unknown }) =>
      serviceClient.post(`${BASE}/telephony/numbers/${phoneId}/members`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUpdateMemberApiV1TelephonyNumbersPhoneIdMembersMemberUserIdPatch(
  options?: MutationOpts<{ phoneId: string; memberUserId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({
      phoneId,
      memberUserId,
      data,
    }: {
      phoneId: string
      memberUserId: string
      data: unknown
    }) =>
      serviceClient.patch(`${BASE}/telephony/numbers/${phoneId}/members/${memberUserId}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useRemoveMemberApiV1TelephonyNumbersPhoneIdMembersMemberUserIdDelete(
  options?: MutationOpts<{ phoneId: string; memberUserId: string }>,
) {
  return useMutation({
    mutationFn: ({ phoneId, memberUserId }: { phoneId: string; memberUserId: string }) =>
      serviceClient.delete(`${BASE}/telephony/numbers/${phoneId}/members/${memberUserId}`, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useAddMonitorApiV1TelephonyNumbersPhoneIdMonitorsPost(
  options?: MutationOpts<{ phoneId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ phoneId, data }: { phoneId: string; data: unknown }) =>
      serviceClient.post(`${BASE}/telephony/numbers/${phoneId}/monitors`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUpdateMonitorApiV1TelephonyNumbersPhoneIdMonitorsMonitorUserIdPatch(
  options?: MutationOpts<{ phoneId: string; monitorUserId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({
      phoneId,
      monitorUserId,
      data,
    }: {
      phoneId: string
      monitorUserId: string
      data: unknown
    }) =>
      serviceClient.patch(`${BASE}/telephony/numbers/${phoneId}/monitors/${monitorUserId}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useRemoveMonitorApiV1TelephonyNumbersPhoneIdMonitorsMonitorUserIdDelete(
  options?: MutationOpts<{ phoneId: string; monitorUserId: string }>,
) {
  return useMutation({
    mutationFn: ({ phoneId, monitorUserId }: { phoneId: string; monitorUserId: string }) =>
      serviceClient.delete(`${BASE}/telephony/numbers/${phoneId}/monitors/${monitorUserId}`, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useAddIbrKeyApiV1TelephonyNumbersPhoneIdIbrKeysPost(
  options?: MutationOpts<{ phoneId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ phoneId, data }: { phoneId: string; data: unknown }) =>
      serviceClient.post(`${BASE}/telephony/numbers/${phoneId}/ibr-keys`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUpdateIbrKeyApiV1TelephonyNumbersPhoneIdIbrKeysKeyNumberPatch(
  options?: MutationOpts<{ phoneId: string; keyNumber: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({
      phoneId,
      keyNumber,
      data,
    }: {
      phoneId: string
      keyNumber: string
      data: unknown
    }) =>
      serviceClient.patch(`${BASE}/telephony/numbers/${phoneId}/ibr-keys/${keyNumber}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useDeleteIbrKeyApiV1TelephonyNumbersPhoneIdIbrKeysKeyNumberDelete(
  options?: MutationOpts<{ phoneId: string; keyNumber: string }>,
) {
  return useMutation({
    mutationFn: ({ phoneId, keyNumber }: { phoneId: string; keyNumber: string }) =>
      serviceClient.delete(`${BASE}/telephony/numbers/${phoneId}/ibr-keys/${keyNumber}`, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useListAvailableNumbersApiV1TelephonyNumbersAvailableGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/telephony/numbers/available`, params],
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/telephony/numbers/available`, {
        params: orgParams(params),
        signal,
      }),
    ...options?.query,
  })
}

export function useBuyNumberApiV1TelephonyNumbersBuyPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/telephony/numbers/buy`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export const getListAvailablePremiumNumbersApiV1TelephonyNumbersPremiumAvailableGetQueryKey =
  () => [`/api/v1/telephony/numbers/premium/available`]

export function useListAvailablePremiumNumbersApiV1TelephonyNumbersPremiumAvailableGet(
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<AvailablePremiumNumber[]>>({
    queryKey: getListAvailablePremiumNumbersApiV1TelephonyNumbersPremiumAvailableGetQueryKey(),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/telephony/numbers/premium/available`, {
        params: orgParams(),
        signal,
      }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<AvailablePremiumNumber[]>>>),
  })
}

export function useBuyPremiumNumberApiV1TelephonyNumbersPremiumBuyPost(
  options?: MutationOpts<{ data: BuyPremiumRequest }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: BuyPremiumRequest }) =>
      serviceClient.post(`${BASE}/telephony/numbers/premium/buy`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export const getListBrandsRouteApiV1BrandsGetQueryKey = (params?: Record<string, unknown>) =>
  params ? [`/api/v1/brands`, params] : [`/api/v1/brands`]

export function useListBrandsRouteApiV1BrandsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getListBrandsRouteApiV1BrandsGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/brands`, { params: orgParams(params), signal }),
    ...options?.query,
  })
}

export function useGetBrandRouteApiV1BrandsBrandIdGet(brandId: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/brands/${brandId}`],
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/brands/${brandId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useRegisterBrandApiV1BrandsPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/brands`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

// 10DLC campaigns. The product moved these to `/api/v1/telnyx/campaigns` so the
// outreach campaigns could own `/api/v1/campaigns`; the names and query keys
// follow it, while the console twin stays at `/product/campaigns`.
export const getListCampaignsRouteApiV1TelnyxCampaignsGetQueryKey = (
  params?: Record<string, unknown>,
) => (params ? [`/api/v1/telnyx/campaigns`, params] : [`/api/v1/telnyx/campaigns`])

export function useListCampaignsRouteApiV1TelnyxCampaignsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AxiosResponse<TelnyxCampaignResponse[]>>({
    queryKey: getListCampaignsRouteApiV1TelnyxCampaignsGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/campaigns`, { params: orgParams(params), signal }),
    ...(options?.query as Partial<UseQueryOptions<AxiosResponse<TelnyxCampaignResponse[]>>>),
  })
}

export const getGetCampaignRouteApiV1TelnyxCampaignsCampaignIdGetQueryKey = (campaignId: string) => [
  `/api/v1/telnyx/campaigns/${campaignId}`,
]

export function useGetCampaignRouteApiV1TelnyxCampaignsCampaignIdGet(
  campaignId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getGetCampaignRouteApiV1TelnyxCampaignsCampaignIdGetQueryKey(campaignId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/campaigns/${campaignId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useRegisterCampaignApiV1TelnyxCampaignsPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/campaigns`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useEditCampaignRouteApiV1TelnyxCampaignsCampaignIdPatch(
  options?: MutationOpts<{ campaignId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/campaigns/${campaignId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useAppealCampaignRouteApiV1TelnyxCampaignsCampaignIdAppealPost(
  options?: MutationOpts<{ campaignId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: unknown }) =>
      serviceClient.post(`${BASE}/campaigns/${campaignId}/appeal`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useAssignNumbersRouteApiV1TelnyxCampaignsCampaignIdNumbersPost(
  options?: MutationOpts<{ campaignId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: unknown }) =>
      serviceClient.post(`${BASE}/campaigns/${campaignId}/numbers`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export const getListPortingApiV1PortingGetQueryKey = (params?: Record<string, unknown>) =>
  params ? [`/api/v1/porting`, params] : [`/api/v1/porting`]

export function useListPortingApiV1PortingGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getListPortingApiV1PortingGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/porting`, { params: orgParams(params), signal }),
    ...options?.query,
  })
}

export function useGetPortingApiV1PortingPortingIdGet(portingId: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/porting/${portingId}`],
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/porting/${portingId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useCheckPortabilityApiV1PortingCheckPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/porting/check`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useCreatePortingApiV1PortingPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/porting`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdatePortingApiV1PortingPortingIdPatch(
  options?: MutationOpts<{ portingId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ portingId, data }: { portingId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/porting/${portingId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useSubmitPortingApiV1PortingPortingIdSubmitPost(
  options?: MutationOpts<{ portingId: string }>,
) {
  return useMutation({
    mutationFn: ({ portingId }: { portingId: string }) =>
      serviceClient.post(`${BASE}/porting/${portingId}/submit`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUploadPortingDocumentApiV1PortingDocumentsPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/porting/documents`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function downloadPortingLoaApiV1PortingPortingIdLoaGet(
  portingId: string,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/porting/${portingId}/loa`, {
    params: orgParams(),
    responseType: 'blob',
    signal,
  })
}

export const getListRulesApiV1AutomationStatusRulesGetQueryKey = () => [
  `/api/v1/automation/status-rules`,
]

export function useListRulesApiV1AutomationStatusRulesGet(options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: getListRulesApiV1AutomationStatusRulesGetQueryKey(),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/automation/status-rules`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useCreateRuleApiV1AutomationStatusRulesPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/automation/status-rules`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateRuleApiV1AutomationStatusRulesRuleIdPatch(
  options?: MutationOpts<{ ruleId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/automation/status-rules/${ruleId}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useDeleteRuleApiV1AutomationStatusRulesRuleIdDelete(
  options?: MutationOpts<{ ruleId: string }>,
) {
  return useMutation({
    mutationFn: ({ ruleId }: { ruleId: string }) =>
      serviceClient.delete(`${BASE}/automation/status-rules/${ruleId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useReorderRulesApiV1AutomationStatusRulesReorderPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/automation/status-rules/reorder`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export const getListAlertsRouteApiV1AutomationComplianceAlertsGetQueryKey = () => [
  `/api/v1/automation/compliance-alerts`,
]

export function useListAlertsRouteApiV1AutomationComplianceAlertsGet(options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: getListAlertsRouteApiV1AutomationComplianceAlertsGetQueryKey(),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/automation/compliance-alerts`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useCreateAlertRouteApiV1AutomationComplianceAlertsPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/automation/compliance-alerts`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateAlertRouteApiV1AutomationComplianceAlertsAlertIdPatch(
  options?: MutationOpts<{ alertId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ alertId, data }: { alertId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/automation/compliance-alerts/${alertId}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useDeleteAlertRouteApiV1AutomationComplianceAlertsAlertIdDelete(
  options?: MutationOpts<{ alertId: string }>,
) {
  return useMutation({
    mutationFn: ({ alertId }: { alertId: string }) =>
      serviceClient.delete(`${BASE}/automation/compliance-alerts/${alertId}`, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export const getGetQuietHoursApiV1AutomationQuietHoursGetQueryKey = () => [
  `/api/v1/automation/quiet-hours`,
]

export function useGetQuietHoursApiV1AutomationQuietHoursGet(options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: getGetQuietHoursApiV1AutomationQuietHoursGetQueryKey(),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/automation/quiet-hours`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function usePatchQuietHoursApiV1AutomationQuietHoursPatch(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.patch(`${BASE}/automation/quiet-hours`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

/** The Insights → Messaging tab's waste-blocked card. Named for its product
 *  twin so the vendored screen calls it unchanged. */
export function useGetMessageSavingsApiV1InsightsMessageSavingsGet(options?: {
  query?: Partial<UseQueryOptions<AxiosResponse<MessageSavingsResponse>>> & { enabled?: boolean }
}) {
  return useQuery<AxiosResponse<MessageSavingsResponse>>({
    queryKey: ['/api/v1/insights/message-savings'],
    queryFn: () =>
      serviceClient.get(`${BASE}/insights/message-savings`, { params: orgParams() }),
    ...options?.query,
  })
}

export const getLiveCallsNowApiV1CallsLiveGetQueryKey = () => ['/api/v1/calls/live']

/** The Insights "Live now" strip's initial load. Real-time updates arrive over
 *  the socket in the product; the console shows the snapshot. */
export function useLiveCallsNowApiV1CallsLiveGet(options?: {
  query?: Partial<UseQueryOptions<AxiosResponse<LiveCallRead[]>>> & { enabled?: boolean }
}) {
  return useQuery<AxiosResponse<LiveCallRead[]>>({
    queryKey: getLiveCallsNowApiV1CallsLiveGetQueryKey(),
    queryFn: () => serviceClient.get(`${BASE}/calls/live`, { params: orgParams() }),
    ...options?.query,
  })
}

// ── Outreach campaigns ────────────────────────────────────────────────────────
// The product's `/api/v1/campaigns` pages, vendored verbatim under
// `pages/campaign`, read and write through these. Every call goes to the console
// twin at `/product/outreach-campaigns` (the plain `/product/campaigns` path is
// the 10DLC twin) with the scoped tenant; the query keys stay the product's own,
// so `invalidateCampaignQueries` finds them.
const OUTREACH = `${BASE}/outreach-campaigns`

type Opts<T> = {
  query?: Partial<UseQueryOptions<AxiosResponse<T>>> & { enabled?: boolean }
}
type Mut<V> = { mutation?: UseMutationOptions<AnyResponse, unknown, V> }

function withParams(key: string, params?: unknown) {
  return params ? [key, params] : [key]
}

export const countAudienceApiV1CampaignsAudienceCountPost = (
  campaignAudienceQuery: CampaignAudienceQuery,
  signal?: AbortSignal,
) =>
  serviceClient.post<CampaignAudienceCount>(`${OUTREACH}/audience/count`, campaignAudienceQuery, {
    params: orgParams(),
    signal,
  })

export const previewDraftStepApiV1CampaignsCampaignIdStepsPreviewPost = (
  campaignId: string,
  campaignStepDraft: CampaignStepDraft,
  signal?: AbortSignal,
) =>
  serviceClient.post<CampaignStepPreview>(
    `${OUTREACH}/${campaignId}/steps/preview`,
    campaignStepDraft,
    { params: orgParams(), signal },
  )

export const getListCampaignRowsApiV1CampaignsRowsGetQueryKey = (
  params?: ListCampaignRowsApiV1CampaignsRowsGetParams,
) => withParams(`/api/v1/campaigns/rows`, params)

export function useListCampaignRowsApiV1CampaignsRowsGet(
  params?: ListCampaignRowsApiV1CampaignsRowsGetParams,
  options?: Opts<CampaignIndexRead>,
) {
  return useQuery<AxiosResponse<CampaignIndexRead>>({
    queryKey: getListCampaignRowsApiV1CampaignsRowsGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/rows`, { params: orgParams({ ...params }), signal }),
    ...options?.query,
  })
}

export const getCampaignsSummaryApiV1CampaignsSummaryGetQueryKey = () => [`/api/v1/campaigns/summary`]

export function useCampaignsSummaryApiV1CampaignsSummaryGet(options?: Opts<CampaignSummaryRead>) {
  return useQuery<AxiosResponse<CampaignSummaryRead>>({
    queryKey: getCampaignsSummaryApiV1CampaignsSummaryGetQueryKey(),
    queryFn: ({ signal }) => serviceClient.get(`${OUTREACH}/summary`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export const getCampaignFilterFieldsApiV1CampaignsFilterFieldsGetQueryKey = (
  params?: CampaignFilterFieldsApiV1CampaignsFilterFieldsGetParams,
) => withParams(`/api/v1/campaigns/filter-fields`, params)

export function useCampaignFilterFieldsApiV1CampaignsFilterFieldsGet(
  params?: CampaignFilterFieldsApiV1CampaignsFilterFieldsGetParams,
  options?: Opts<CampaignFilterField[]>,
) {
  return useQuery<AxiosResponse<CampaignFilterField[]>>({
    queryKey: getCampaignFilterFieldsApiV1CampaignsFilterFieldsGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/filter-fields`, { params: { ...params }, signal }),
    ...options?.query,
  })
}

export const getCampaignTemplatesApiV1CampaignsTemplatesGetQueryKey = (
  params: CampaignTemplatesApiV1CampaignsTemplatesGetParams,
) => [`/api/v1/campaigns/templates`, params]

export function useCampaignTemplatesApiV1CampaignsTemplatesGet(
  params: CampaignTemplatesApiV1CampaignsTemplatesGetParams,
  options?: Opts<CampaignTemplateOption[]>,
) {
  return useQuery<AxiosResponse<CampaignTemplateOption[]>>({
    queryKey: getCampaignTemplatesApiV1CampaignsTemplatesGetQueryKey(params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/templates`, { params: orgParams({ ...params }), signal }),
    ...options?.query,
  })
}

export const getGetCampaignApiV1CampaignsCampaignIdGetQueryKey = (campaignId: string) => [
  `/api/v1/campaigns/${campaignId}`,
]

export function useGetCampaignApiV1CampaignsCampaignIdGet(
  campaignId: string,
  options?: Opts<CampaignRead>,
) {
  return useQuery<AxiosResponse<CampaignRead>>({
    queryKey: getGetCampaignApiV1CampaignsCampaignIdGetQueryKey(campaignId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/${campaignId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export const getCampaignOverviewApiV1CampaignsCampaignIdOverviewGetQueryKey = (
  campaignId: string,
) => [`/api/v1/campaigns/${campaignId}/overview`]

export function useCampaignOverviewApiV1CampaignsCampaignIdOverviewGet(
  campaignId: string,
  options?: Opts<CampaignOverviewRead>,
) {
  return useQuery<AxiosResponse<CampaignOverviewRead>>({
    queryKey: getCampaignOverviewApiV1CampaignsCampaignIdOverviewGetQueryKey(campaignId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/${campaignId}/overview`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export const getCampaignSequenceRulesApiV1CampaignsCampaignIdSequenceGetQueryKey = (
  campaignId: string,
) => [`/api/v1/campaigns/${campaignId}/sequence`]

export function useCampaignSequenceRulesApiV1CampaignsCampaignIdSequenceGet(
  campaignId: string,
  options?: Opts<CampaignSequenceRead>,
) {
  return useQuery<AxiosResponse<CampaignSequenceRead>>({
    queryKey: getCampaignSequenceRulesApiV1CampaignsCampaignIdSequenceGetQueryKey(campaignId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/${campaignId}/sequence`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export const getListContactsApiV1CampaignsCampaignIdContactsGetQueryKey = (
  campaignId: string,
  params?: ListContactsApiV1CampaignsCampaignIdContactsGetParams,
) => withParams(`/api/v1/campaigns/${campaignId}/contacts`, params)

export function useListContactsApiV1CampaignsCampaignIdContactsGet(
  campaignId: string,
  params?: ListContactsApiV1CampaignsCampaignIdContactsGetParams,
  options?: Opts<CampaignContactsRead>,
) {
  return useQuery<AxiosResponse<CampaignContactsRead>>({
    queryKey: getListContactsApiV1CampaignsCampaignIdContactsGetQueryKey(campaignId, params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/${campaignId}/contacts`, {
        params: orgParams({ ...params }),
        signal,
      }),
    ...options?.query,
  })
}

export const getListActivityApiV1CampaignsCampaignIdActivityGetQueryKey = (
  campaignId: string,
  params?: ListActivityApiV1CampaignsCampaignIdActivityGetParams,
) => withParams(`/api/v1/campaigns/${campaignId}/activity`, params)

export function useListActivityApiV1CampaignsCampaignIdActivityGet(
  campaignId: string,
  params?: ListActivityApiV1CampaignsCampaignIdActivityGetParams,
  options?: Opts<CampaignActivityRead>,
) {
  return useQuery<AxiosResponse<CampaignActivityRead>>({
    queryKey: getListActivityApiV1CampaignsCampaignIdActivityGetQueryKey(campaignId, params),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/${campaignId}/activity`, {
        params: orgParams({ ...params }),
        signal,
      }),
    ...options?.query,
  })
}

export const getCampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGetQueryKey = (
  campaignId: string,
  params?: CampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGetParams,
) => withParams(`/api/v1/campaigns/${campaignId}/deliverability`, params)

export function useCampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGet(
  campaignId: string,
  params?: CampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGetParams,
  options?: Opts<CampaignDeliverabilityRead>,
) {
  return useQuery<AxiosResponse<CampaignDeliverabilityRead>>({
    queryKey: getCampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGetQueryKey(
      campaignId,
      params,
    ),
    queryFn: ({ signal }) =>
      serviceClient.get(`${OUTREACH}/${campaignId}/deliverability`, {
        params: orgParams({ ...params }),
        signal,
      }),
    ...options?.query,
  })
}

export function useCreateCampaignApiV1CampaignsPost(options?: Mut<{ data: CampaignCreate }>) {
  return useMutation({
    mutationFn: ({ data }: { data: CampaignCreate }) =>
      serviceClient.post<CampaignRead>(OUTREACH, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdateCampaignApiV1CampaignsCampaignIdPatch(
  options?: Mut<{ campaignId: string; data: CampaignUpdate }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: CampaignUpdate }) =>
      serviceClient.patch<CampaignRead>(`${OUTREACH}/${campaignId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useDeleteCampaignApiV1CampaignsCampaignIdDelete(
  options?: Mut<{ campaignId: string }>,
) {
  return useMutation({
    mutationFn: ({ campaignId }: { campaignId: string }) =>
      serviceClient.delete(`${OUTREACH}/${campaignId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useDuplicateCampaignApiV1CampaignsCampaignIdDuplicatePost(
  options?: Mut<{ campaignId: string }>,
) {
  return useMutation({
    mutationFn: ({ campaignId }: { campaignId: string }) =>
      serviceClient.post<CampaignRead>(`${OUTREACH}/${campaignId}/duplicate`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useStartCampaignApiV1CampaignsCampaignIdStartPost(
  options?: Mut<{ campaignId: string; data: StartCampaignApiV1CampaignsCampaignIdStartPostBody }>,
) {
  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string
      data: StartCampaignApiV1CampaignsCampaignIdStartPostBody
    }) =>
      serviceClient.post<CampaignRead>(`${OUTREACH}/${campaignId}/start`, data ?? undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function usePauseCampaignApiV1CampaignsCampaignIdPausePost(
  options?: Mut<{ campaignId: string }>,
) {
  return useMutation({
    mutationFn: ({ campaignId }: { campaignId: string }) =>
      serviceClient.post<CampaignRead>(`${OUTREACH}/${campaignId}/pause`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useStopCampaignApiV1CampaignsCampaignIdStopPost(
  options?: Mut<{ campaignId: string }>,
) {
  return useMutation({
    mutationFn: ({ campaignId }: { campaignId: string }) =>
      serviceClient.post<CampaignRead>(`${OUTREACH}/${campaignId}/stop`, undefined, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useAddStepApiV1CampaignsCampaignIdStepsPost(
  options?: Mut<{ campaignId: string; data: CampaignStepCreate }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: CampaignStepCreate }) =>
      serviceClient.post<CampaignStepRead>(`${OUTREACH}/${campaignId}/steps`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useUpdateStepApiV1CampaignsCampaignIdStepsStepIdPatch(
  options?: Mut<{ campaignId: string; stepId: string; data: CampaignStepUpdate }>,
) {
  return useMutation({
    mutationFn: ({
      campaignId,
      stepId,
      data,
    }: {
      campaignId: string
      stepId: string
      data: CampaignStepUpdate
    }) =>
      serviceClient.patch<CampaignStepRead>(`${OUTREACH}/${campaignId}/steps/${stepId}`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useDeleteStepApiV1CampaignsCampaignIdStepsStepIdDelete(
  options?: Mut<{ campaignId: string; stepId: string }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, stepId }: { campaignId: string; stepId: string }) =>
      serviceClient.delete(`${OUTREACH}/${campaignId}/steps/${stepId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useAddPeopleApiV1CampaignsCampaignIdPeoplePost(
  options?: Mut<{ campaignId: string; data: CampaignPeopleAdd }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: CampaignPeopleAdd }) =>
      serviceClient.post<CampaignPeopleAddResult>(`${OUTREACH}/${campaignId}/people`, data, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useRemovePeopleApiV1CampaignsCampaignIdPeopleRemovePost(
  options?: Mut<{ campaignId: string; data: CampaignPeopleRemove }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: CampaignPeopleRemove }) =>
      serviceClient.post<CampaignPeopleRemoveResult>(
        `${OUTREACH}/${campaignId}/people/remove`,
        data,
        { params: orgParams() },
      ),
    ...options?.mutation,
  })
}

export function useAssignPeopleApiV1CampaignsCampaignIdPeopleAssignPost(
  options?: Mut<{ campaignId: string; data: CampaignPeopleAssign }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: CampaignPeopleAssign }) =>
      serviceClient.post<CampaignPeopleAssignResult>(
        `${OUTREACH}/${campaignId}/people/assign`,
        data,
        { params: orgParams() },
      ),
    ...options?.mutation,
  })
}

export function useCreatePeopleTasksApiV1CampaignsCampaignIdPeopleTasksPost(
  options?: Mut<{ campaignId: string; data: CampaignPeopleTasks }>,
) {
  return useMutation({
    mutationFn: ({ campaignId, data }: { campaignId: string; data: CampaignPeopleTasks }) =>
      serviceClient.post<CampaignPeopleTasksResult>(
        `${OUTREACH}/${campaignId}/people/tasks`,
        data,
        { params: orgParams() },
      ),
    ...options?.mutation,
  })
}

export function useCampaignPowerDialApiV1CampaignsCampaignIdPowerDialPost(
  options?: Mut<{
    campaignId: string
    params?: CampaignPowerDialApiV1CampaignsCampaignIdPowerDialPostParams
  }>,
) {
  return useMutation({
    mutationFn: ({
      campaignId,
      params,
    }: {
      campaignId: string
      params?: CampaignPowerDialApiV1CampaignsCampaignIdPowerDialPostParams
    }) =>
      serviceClient.post<MassCallResponse>(`${OUTREACH}/${campaignId}/power-dial`, undefined, {
        params: orgParams({ ...params }),
      }),
    ...options?.mutation,
  })
}
